import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Table, Tag } from "antd";
import { BsDot } from "react-icons/bs";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppointmentsControl from "../AppointmentsControl";
import useIsMobile from "../../shared/Mobile-Responsive";
import "./index.css";
import { getAppointmentsList, setPageSize } from "../../../api/Appointments/index";
import { transformAppointmentsList, getRiskText, getStatusText, getRiskColor, getStatusColor, getRiskTextColor, transformFiltersForAPI, getRiskStatusBadge } from "../../../../../utils/index";
import PropTypes from "prop-types";

// Status Badge Component (reused from appointments page)
const StatusBadge = ({ status, statusColor }) => {
  return (
    <div className="flex items-center gap-1">
      <div className={`w-3 h-3 rounded-full ${statusColor}`}></div>
      <span className="text-[#4B61FF] text-sm font-semibold font-poppins">
        {status}
      </span>
    </div>
  );
};

// Risk Badge Component (reused from appointments page)
const RiskBadge = ({ riskStatus }) => {
  const badge = getRiskStatusBadge(riskStatus);
  return (
    <div
      className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.5px] font-poppins"
      style={{
        backgroundColor: badge.backgroundColor,
        color: badge.color
      }}
    >
      {badge.text}
    </div>
  );
};

// Mobile Appointment Card Component (using existing design)
const MobileAppointmentCard = ({ appointment, onViewDetails }) => {
  return (
    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm p-3 mb-4 relative font-poppins">
      {/* Checkbox */}
      <div className="absolute top-4 right-4">
        <input type="checkbox" className="w-5 h-5 rounded accent-[#4B61FF]" />
      </div>

      <div className="flex gap-8">
        {/* Labels Column */}
        <div className="flex flex-col gap-3 text-[11px] leading-5 font-semibold text-[#64748B] uppercase tracking-wide">
          <div>TYPE</div>
          <div>DATE</div>
          <div>TIME SLOT</div>
          <div>STATUS</div>
          <div>MODE</div>
          <div>PRACTITIONER</div>
          <div>MEMBER NAME</div>
          <div>RISK</div>
        </div>

        {/* Values Column */}
        <div className="flex flex-col gap-3 flex-1">
          <div className="text-sm font-semibold text-[#171717]">{appointment.type}</div>
          <div className="text-sm text-[#334155]">{appointment.date}</div>
          <div className="text-sm text-[#334155]">{appointment.timeSlot}</div>
          <StatusBadge status={appointment.status} statusColor={appointment.statusColor} />
          <div className="text-sm text-[#334155]">{appointment.mode}</div>
          <div className="text-sm text-[#334155]">{appointment.practitioner}</div>
          <div className="text-sm text-[#334155]">{appointment.memberName}</div>
          <RiskBadge riskStatus={appointment.originalRisk} />
        </div>
      </div>

      {/* Separator */}
      <div className="h-px bg-[#F1F5F9] my-4" />

      {/* View Details Button */}
      <div className="flex justify-end">
        <button
          onClick={() => onViewDetails(appointment.id)}
          className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0 transition-colors"
        >
          <ChevronRight size={14} className="text-[#64748B]" />
        </button>
      </div>
    </div>
  );
};

export default function DoctorAppointmentsTable({ externalStatusFilter }) {
  const [activeTab, setActiveTab] = useState("Upcoming");
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(externalStatusFilter || "all");
  const [dateFilter, setDateFilter] = useState("");
  const [sortConfig, setSortConfig] = useState(null);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  // Redux state
  const { 
    data: appointmentsData, 
    loading, 
    error, 
    totalElements, 
    totalPages, 
    currentPage, 
    pageSize 
  } = useSelector(state => state.appointments.list);
  
  const { filters } = useSelector(state => state.appointments);

  // Sync internal status filter with external prop
  useEffect(() => {
    if (externalStatusFilter !== undefined) {
      setStatusFilter(externalStatusFilter);
    }
  }, [externalStatusFilter]);

  // Transform API data to table format with proper status/risk transformations
  const transformApiDataToTableFormat = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    return apiData.map((item, index) => ({
      key: item.appointmentId || index + 1,
      apptId: item.appointmentId,
      type: item.appointmentType,
      date: item.appointmentDate,
      timeSlot: item.timeSlot,
      status: getStatusText(item.currentStatus), // Transform status for display
      originalStatus: item.currentStatus, // Keep original for filtering
      mode: item.mode,
      practitioner: item.practitionerName,
      memberName: item.memberName,
      risk: getRiskText(item.riskStatus), // Transform risk for display
      originalRisk: item.riskStatus, // Keep original for filtering
      statusColor: getStatusColor(item.currentStatus),
      riskColor: getRiskColor(item.riskStatus),
      riskTextColor: getRiskTextColor(item.riskStatus)
    }));
  };

  // Build combined filters helper
  const buildCombinedFilters = () => {
    const combinedFilters = {
      ...transformFiltersForAPI(appliedFilters)
    };

    // Add status filter if not 'all'
    if (statusFilter && statusFilter !== 'all') {
      combinedFilters.status = statusFilter;
    }

    // Add date filter
    if (dateFilter) {
      combinedFilters.startDate = dateFilter;
      combinedFilters.endDate = dateFilter;
    }

    // Add search text as single name parameter
    if (searchText) {
      combinedFilters.name = searchText;
    }

    return combinedFilters;
  };

  // Load appointments data with optimized dependency handling
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Clear existing timeout for search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce search requests
      if (searchText !== "") {
        searchTimeoutRef.current = setTimeout(() => {
          dispatch(getAppointmentsList({
            page: currentPage,
            size: pageSize,
            filters: buildCombinedFilters()
          }));
        }, 500); // 500ms delay for search
      } else {
        // Immediate dispatch for non-search filters
        dispatch(getAppointmentsList({
          page: currentPage,
          size: pageSize,
          filters: buildCombinedFilters()
        }));
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [dispatch, currentPage, pageSize, appliedFilters, searchText, statusFilter, dateFilter]);

  // Transform and get appointments data with client-side sorting
  const getAppointmentsData = () => {
    let data = transformApiDataToTableFormat(appointmentsData);

    // Apply client-side sorting if sortConfig is provided
    if (sortConfig && sortConfig.field && sortConfig.order) {
      data = [...data].sort((a, b) => {
        const field = sortConfig.field;
        let valA = a[field];
        let valB = b[field];

        // Handle null/undefined values
        if (!valA) valA = "";
        if (!valB) valB = "";

        // Convert to lowercase for string comparison
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (sortConfig.order === 'asc') {
          return valA > valB ? 1 : valA < valB ? -1 : 0;
        } else {
          return valB > valA ? 1 : valB < valA ? -1 : 0;
        }
      });
    }

    return data;
  };

  // Handle search functionality
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Handle status filter
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
  };

  // Handle date filter
  const handleDateFilter = (date) => {
    setDateFilter(date);
  };

  // Handle sort change
  const handleSortChange = (sortData) => {
    setSortConfig(sortData);
  };

  // Handle pagination change
  const handleTableChange = (pagination, filters, sorter) => {
    const token = localStorage.getItem('token');
    if (token) {
      const newPage = pagination.current - 1; // API uses 0-based indexing
      const newSize = pagination.pageSize;

      // Update Redux state first if page size changed
      if (newSize !== pageSize) {
        dispatch(setPageSize(newSize));
      }

      dispatch(getAppointmentsList({
        page: newPage,
        size: newSize,
        filters: buildCombinedFilters()
      }));
    }
  };

  const columns = [
    // { title: "APPT ID", dataIndex: "apptId", key: "apptId", render: (text) => <span className="font-semibold text-gray-800">{text}</span> },
    { title: "TYPE", dataIndex: "type", key: "type", render: (text) => <span className="font-semibold text-[#171717]">{text}</span> },
    { title: "DATE", dataIndex: "date", key: "date" },
    { title: "TIME SLOT", dataIndex: "timeSlot", key: "timeSlot" },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (text, record) => {
        return (
          <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${record.statusColor}`}></div>
            <span className="text-[#4B61FF] text-[13px] font-semibold font-poppins">
              {text}
            </span>
          </div>
        );
      },
    },
    { title: "MODE", dataIndex: "mode", key: "mode" },
    { title: "PRACTITIONER", dataIndex: "practitioner", key: "practitioner" },
    { title: "MEMBER NAME", dataIndex: "memberName", key: "memberName" },
    {
      title: "RISK",
      dataIndex: "risk",
      key: "risk",
      render: (risk, record) => {
        // Map the transformed risk text back to the CSS class
        const getRiskCssClass = (riskText) => {
          if (riskText === "HIGH") return "risk-high";
          if (riskText === "MODERATE") return "risk-moderate";
          if (riskText === "LOW") return "risk-low";
          if (riskText === "NO RISK") return "risk-no-risk";
          return "risk-na"; // For N/A or any other value
        };

        return <Tag className={`risk-tag ${getRiskCssClass(risk)}`}>{risk}</Tag>;
      },
    },
    { 
      title: "", 
      key: "action", 
      render: (_, record) => (
        <button
          type="button"
          onClick={() => navigate(`/appointment-detail/${record.key}`)}
          className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0 cursor-pointer transition-colors"
          aria-label="View appointment details"
        >
          <ChevronRight size={14} className="text-[#64748B]" />
        </button>
      )
    },
  ];

  return (
    <>
      <AppointmentsControl
        onFiltersChange={setAppliedFilters}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onDateFilter={handleDateFilter}
        currentStatus={statusFilter}
        onSortChange={handleSortChange}
        appliedFilters={appliedFilters}
      />

      <div className="border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9] my-1" />

      <div className="doctor-appt-table">
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB] mx-auto"></div>
            <p className="mt-2 text-[#64748B]">Loading appointments...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-[#DC2626]">
            <p>Error loading appointments: {error}</p>
            <button
              onClick={() => dispatch(getAppointmentsList({ page: currentPage, size: pageSize, filters: buildCombinedFilters() }))}
              className="mt-2 btn-gradient rounded-xl h-9 px-[14px] text-[13px] font-semibold border-0"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Desktop View - Hidden on mobile */}
            <div className="hidden md:block">
              <Table
                rowSelection={{
                  type: "checkbox",
                  onChange: (selectedRowKeys, selectedRows) => {
                    console.log("Selected:", selectedRowKeys, selectedRows);
                  },
                }}
                columns={columns}
                dataSource={getAppointmentsData()}
                loading={loading}
                pagination={{
                  current: currentPage + 1, // UI uses 1-based indexing
                  pageSize: pageSize,
                  total: totalElements,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "30"],
                  showQuickJumper: false,
                  className: "font-poppins",
                  showTotal: (total, range) =>
                      `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                  position: ["bottomCenter"],
                }}
                onChange={handleTableChange}
                bordered={false}
                rowClassName={() => "bg-white text-[#334155] text-[13px] hover:bg-[#F8FAFC] transition"}
              />
            </div>

            {/* Mobile View - Hidden on desktop */}
            <div className="block md:hidden">
              <div className="p-2">
                {getAppointmentsData().length > 0 ? (
                  getAppointmentsData().map((appointment) => (
                    <MobileAppointmentCard
                      key={appointment.key}
                      appointment={appointment}
                      onViewDetails={(id) => navigate(`/appointment-detail/${id}`)}
                    />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#64748B]">No appointments found</p>
                  </div>
                )}
              </div>

              {/* Mobile Pagination */}
              <div className="flex flex-col gap-4 p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#64748B]">Show on page</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handleTableChange({ current: 1, pageSize: parseInt(e.target.value) })}
                      className="text-xs border-[1px] border-solid border-[#E2E8F0] rounded-xl bg-white text-[#334155] px-2 py-1"
                    >
                      {["5", "10", "20", "30"].map(size => (
                        <option key={size} value={size}>{size} Rows</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-xs text-[#64748B]">
                    Showing {((currentPage) * pageSize) + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} entries
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    className="px-4 py-2 text-xs font-semibold text-[#334155] bg-white border-0 disabled:opacity-50"
                    disabled={currentPage === 0}
                    onClick={() => handleTableChange({ current: currentPage, pageSize })}
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#64748B]">
                      Page {currentPage + 1} of {Math.ceil(totalElements / pageSize)}
                    </span>
                  </div>

                  <button
                    className="px-4 py-2 text-xs font-semibold text-[#334155] bg-white border-0 disabled:opacity-50"
                    disabled={(currentPage + 1) * pageSize >= totalElements}
                    onClick={() => handleTableChange({ current: currentPage + 2, pageSize })}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

DoctorAppointmentsTable.propTypes = {
  externalStatusFilter: PropTypes.string
};
